import { useState, useEffect } from 'react';
import api from '../services/api';

export interface DropdownOption {
  _id: string;
  category: string;
  value: string;
  label_DE: string;
  label_EN?: string;
  sortOrder: number;
  isActive?: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

export type DropdownCategory = 
  | 'contentUnit' 
  | 'unit' 
  | 'baseUnit' 
  | 'baseComparisonUnitCode' 
  | 'unitDescriptor'
  | 'country'
  | 'storageZone'
  | 'packagingUnit'
  | 'brand'
  | 'ecrCode'
  | 'labels'
  | 'fangzone'
  | 'fangmethode'
  | 'line'
  | 'dangerousGoods';

export const useDropdownOptions = (category?: DropdownCategory) => {
  const [options, setOptions] = useState<DropdownOption[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // **GET - Optionen für eine Kategorie laden**
  const fetchOptions = async (cat: DropdownCategory) => {
    console.log(`[useDropdownOptions] fetchOptions called for category: ${cat}`);
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/dropdown-options/${cat}`);
      console.log(`[useDropdownOptions] API response for ${cat}:`, response.data);
      if (response.data.success) {
        setOptions(response.data.data);
        console.log(`[useDropdownOptions] Options set for ${cat}:`, response.data.data);
      }
    } catch (err: any) {
      console.error(`[useDropdownOptions] Error fetching ${cat}:`, err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Dropdown-Optionen');
      console.error('Error fetching dropdown options:', err);
    } finally {
      setLoading(false);
    }
  };

  // **GET - Alle verfügbaren Kategorien laden**
  const fetchCategories = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/dropdown-options/categories');
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Laden der Kategorien');
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  };

  // **POST - Neue Option erstellen**
  const createOption = async (optionData: Partial<DropdownOption>) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/dropdown-options', optionData);
      if (response.data.success) {
        if (category) {
          await fetchOptions(category); // Refresh list
        }
        return response.data.data;
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Erstellen der Option');
      console.error('Error creating option:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // **PUT - Option aktualisieren**
  const updateOption = async (optionId: string, updateData: Partial<DropdownOption>) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.put(`/dropdown-options/${optionId}`, updateData);
      if (response.data.success) {
        if (category) {
          await fetchOptions(category); // Refresh list
        }
        return response.data.data;
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Aktualisieren der Option');
      console.error('Error updating option:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // **DELETE - Option löschen**
  const deleteOption = async (optionId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.delete(`/dropdown-options/${optionId}`);
      if (response.data.success) {
        if (category) {
          await fetchOptions(category); // Refresh list
        }
        return true;
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Löschen der Option');
      console.error('Error deleting option:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // **PUT - Sortierung aktualisieren**
  const updateSortOrder = async (cat: DropdownCategory, sortedIds: string[]) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.put(`/dropdown-options/${cat}/sort`, { sortedIds });
      if (response.data.success) {
        setOptions(response.data.data);
        return response.data.data;
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Aktualisieren der Sortierung');
      console.error('Error updating sort order:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // **Auto-load options wenn category gesetzt ist**
  useEffect(() => {
    if (category) {
      fetchOptions(category);
    }
  }, [category]);

  return {
    options,
    categories,
    loading,
    error,
    fetchOptions,
    fetchCategories,
    createOption,
    updateOption,
    deleteOption,
    updateSortOrder,
    // Helper für einfache Dropdown-Lists
    getOptionsForSelect: () => options.map(opt => ({
      value: opt.value,
      label: opt.label_DE
    }))
  };
};
